import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    ArrowPathIcon,
    XMarkIcon,
    PhotoIcon,
    BellSlashIcon,
    ExclamationTriangleIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    WrenchScrewdriverIcon,
    LifebuoyIcon,
    MegaphoneIcon,
    CheckIcon,
} from "@heroicons/react/24/outline";
import getNotifications, { getNotificationCounts } from "../api/getNotifications";
import deleteNotification from "../api/deleteNotification";
import editNotification from "../api/editNotification";
import LangTabs from "../components/LangTabs";
import { uploadChatFile } from "../api/operator";

const TYPES = [
    { value: "service", label: "Сервис", countKey: "service_count", icon: WrenchScrewdriverIcon, tone: "text-sky-500 bg-sky-500/15" },
    { value: "support", label: "Поддержка", countKey: "support_count", icon: LifebuoyIcon, tone: "text-emerald-500 bg-emerald-500/15" },
    { value: "promotions", label: "Акции", countKey: "promotions_count", icon: MegaphoneIcon, tone: "text-amber-500 bg-amber-500/15" },
];

const PAGE_SIZE = 12;
const MAX_IMAGE_MB = 10;

const inputCls =
    "w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary " +
    "placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30";

/** file может быть URL или base64 (старые уведомления) — как в mobile/web */
const imageSrc = (file) => {
    if (!file) return "";
    if (file.startsWith("data:") || file.startsWith("http://") || file.startsWith("https://")) return file;
    return `data:image/jpeg;base64,${file}`;
};

const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleString("ru-RU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

function ManageNotifications() {
    const navigate = useNavigate();
    const [type, setType] = useState("service");
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({ total: 0, pages: 0 });
    const [counts, setCounts] = useState(null);
    const [state, setState] = useState("loading"); // loading | ready | error
    const [toast, setToast] = useState(null);
    const [editing, setEditing] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        if (!toast || toast.type === "error") return undefined;
        const t = setTimeout(() => setToast(null), 3000);
        return () => clearTimeout(t);
    }, [toast]);

    const loadCounts = useCallback(async () => {
        try {
            setCounts(await getNotificationCounts());
        } catch (err) {
            console.error("Ошибка при получении счётчиков:", err);
        }
    }, []);

    const load = useCallback(async () => {
        setState((s) => (s === "ready" ? "ready" : "loading"));
        try {
            const data = await getNotifications({ notification_type: type, page, limit: PAGE_SIZE });
            setItems(data.items || []);
            setMeta({ total: data.total || 0, pages: data.pages || 0 });
            setState("ready");
        } catch (err) {
            console.error("Ошибка при получении уведомлений:", err);
            setState("error");
        }
    }, [type, page]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        loadCounts();
    }, [loadCounts]);

    const switchType = (value) => {
        setType(value);
        setPage(1);
        setItems([]);
        setState("loading");
        setConfirmDeleteId(null);
    };

    const handleDelete = async (n) => {
        setBusyId(n.id);
        try {
            await deleteNotification(n.id);
            setToast({ type: "success", text: `«${n.title}» удалено` });
            setConfirmDeleteId(null);
            // Последний элемент страницы — возвращаемся на предыдущую
            if (items.length === 1 && page > 1) setPage((p) => p - 1);
            else load();
            loadCounts();
        } catch (err) {
            setToast({ type: "error", text: err.message });
        } finally {
            setBusyId(null);
        }
    };

    const handleSaved = (updated, movedType) => {
        setEditing(null);
        setToast({
            type: "success",
            text: movedType ? `Перенесено в «${TYPES.find((t) => t.value === movedType)?.label}»` : "Изменения сохранены",
        });
        if (movedType) {
            load();
            loadCounts();
        } else {
            setItems((list) => list.map((n) => (n.id === updated.id ? { ...n, ...updated } : n)));
        }
    };

    const from = (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, meta.total);

    return (
        <div className="space-y-4 text-left">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="text-2xl font-bold text-text-primary">Уведомления</h2>
                    <p className="mt-0.5 text-sm text-text-secondary">Лента «Уведомления» в приложении и на сайте</p>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            load();
                            loadCounts();
                        }}
                        title="Обновить"
                        aria-label="Обновить"
                        className="rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary"
                    >
                        <ArrowPathIcon className={`h-5 w-5 ${state === "loading" ? "animate-spin" : ""}`} />
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/add-notification")}
                        className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                    >
                        <PlusIcon className="h-4 w-4" />
                        Новое уведомление
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div role="tablist" className="inline-flex rounded-lg border border-border-primary bg-bg-card p-1">
                {TYPES.map((t) => {
                    const Icon = t.icon;
                    const active = type === t.value;
                    return (
                        <button
                            key={t.value}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => switchType(t.value)}
                            className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                                active ? "bg-accent-grad text-white shadow-sm" : "text-text-secondary hover:text-text-primary"
                            }`}
                        >
                            <Icon className="h-4 w-4" />
                            {t.label}
                            {counts && (
                                <span className={`rounded-full px-1.5 text-xs tabular-nums ${active ? "bg-white/20" : "bg-bg-tertiary text-text-muted"}`}>
                                    {counts[t.countKey] ?? 0}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {toast && (
                <div
                    role={toast.type === "error" ? "alert" : "status"}
                    className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
                        toast.type === "error" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
                    }`}
                >
                    <span>{toast.text}</span>
                    <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>
            )}

            {state === "error" ? (
                <div className="flex flex-col items-center gap-3 rounded-block border border-border-primary bg-bg-card px-6 py-14 text-center">
                    <ExclamationTriangleIcon className="h-10 w-10 text-rose-500/80" />
                    <p className="text-sm text-text-secondary">Не удалось загрузить уведомления</p>
                    <button type="button" onClick={load} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
                        Повторить
                    </button>
                </div>
            ) : state === "loading" && items.length === 0 ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="animate-pulse overflow-hidden rounded-block border border-border-primary bg-bg-card">
                            <div className="aspect-video bg-bg-tertiary" />
                            <div className="space-y-2 p-4">
                                <div className="h-4 w-2/3 rounded bg-bg-tertiary" />
                                <div className="h-3 rounded bg-bg-tertiary" />
                                <div className="h-3 w-1/2 rounded bg-bg-tertiary" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="rounded-block border border-border-primary bg-bg-card px-6 py-16 text-center">
                    <BellSlashIcon className="mx-auto mb-3 h-12 w-12 text-text-muted" />
                    <p className="text-sm font-medium text-text-primary">В разделе «{TYPES.find((t) => t.value === type)?.label}» пока пусто</p>
                    <button type="button" onClick={() => navigate("/add-notification")} className="mt-3 text-sm font-medium text-[#7C3AED] hover:underline dark:text-indigo-300">
                        Создать уведомление
                    </button>
                </div>
            ) : (
                <>
                    <ul className={`grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 ${state === "loading" ? "opacity-60" : ""}`}>
                        {items.map((n) => {
                            const confirming = confirmDeleteId === n.id;
                            const busy = busyId === n.id;
                            return (
                                <li
                                    key={n.id}
                                    className={`group flex flex-col overflow-hidden rounded-block border bg-bg-card shadow-sm transition-colors ${
                                        confirming ? "border-rose-500/50" : "border-border-primary"
                                    } ${busy ? "pointer-events-none opacity-60" : ""}`}
                                >
                                    {n.file ? (
                                        <img src={imageSrc(n.file)} alt="" loading="lazy" className="aspect-video w-full bg-bg-tertiary object-cover" />
                                    ) : (
                                        <div className="flex aspect-video w-full items-center justify-center bg-bg-primary text-text-muted">
                                            <PhotoIcon className="h-8 w-8 opacity-40" />
                                        </div>
                                    )}
                                    <div className="flex flex-1 flex-col p-4">
                                        <h3 className="line-clamp-2 text-base font-semibold text-text-primary" title={n.title}>
                                            {n.title}
                                        </h3>
                                        <p className="mt-1 line-clamp-3 whitespace-pre-line text-sm text-text-secondary">{n.text}</p>
                                        <div className="mt-2 flex gap-1" title="Языки: без перевода пользователь видит RU">
                                            {["ru", "kk", "en"].map((l) => {
                                                const has = l === "ru" || Boolean(n.translations?.[l]);
                                                return (
                                                    <span
                                                        key={l}
                                                        className={`rounded px-1.5 text-[10px] font-semibold ${has ? "bg-emerald-500/15 text-emerald-500" : "bg-bg-tertiary text-text-muted line-through"}`}
                                                    >
                                                        {l === "kk" ? "KZ" : l.toUpperCase()}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                        <div className="mt-auto flex items-center justify-between gap-2 pt-3">
                                            <span className="text-xs text-text-muted">
                                                <span className="font-mono">#{n.id}</span> · {fmtDate(n.created_at)}
                                            </span>
                                            {confirming ? (
                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        autoFocus
                                                        onClick={() => handleDelete(n)}
                                                        className="rounded-lg bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-rose-700"
                                                    >
                                                        Удалить
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfirmDeleteId(null)}
                                                        className="rounded-lg border border-border-primary px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-bg-tertiary"
                                                    >
                                                        Нет
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditing(n)}
                                                        title="Редактировать"
                                                        aria-label="Редактировать"
                                                        className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
                                                    >
                                                        <PencilSquareIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfirmDeleteId(n.id)}
                                                        title="Удалить"
                                                        aria-label="Удалить"
                                                        className="rounded-lg p-1.5 text-text-muted hover:bg-rose-500/10 hover:text-rose-500"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {confirming && (
                                            <p className="mt-2 text-xs text-rose-500">Уведомление исчезнет из ленты у всех. Уже отправленные push отозвать нельзя.</p>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>

                    {meta.pages > 1 && (
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-xs text-text-secondary">
                                {from}–{to} из {meta.total}
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                    aria-label="Предыдущая страница"
                                    className="rounded-lg border border-border-primary p-1.5 text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <ChevronLeftIcon className="h-4 w-4" />
                                </button>
                                <span className="min-w-[4.5rem] text-center text-sm font-medium text-text-primary">
                                    {page} / {meta.pages}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => p + 1)}
                                    disabled={page >= meta.pages}
                                    aria-label="Следующая страница"
                                    className="rounded-lg border border-border-primary p-1.5 text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <ChevronRightIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {editing && <EditDialog notification={editing} currentType={type} onClose={() => setEditing(null)} onSaved={handleSaved} />}
        </div>
    );
}

const EditDialog = ({ notification, currentType, onClose, onSaved }) => {
    const [form, setForm] = useState({ title: notification.title || "", text: notification.text || "", type: currentType });
    const initialTr = {
        kk: { title: notification.translations?.kk?.title || "", text: notification.translations?.kk?.text || "" },
        en: { title: notification.translations?.en?.title || "", text: notification.translations?.en?.text || "" },
    };
    const [tr, setTr] = useState(initialTr);
    const [lang, setLang] = useState("ru");
    const trStatus = (t) => (t.title.trim() && t.text.trim() ? "filled" : t.title.trim() || t.text.trim() ? "partial" : "empty");
    const status = { ru: "filled", kk: trStatus(tr.kk), en: trStatus(tr.en) };
    const partialLang = ["kk", "en"].find((l) => status[l] === "partial");
    const fieldValue = (key) => (lang === "ru" ? form[key] : tr[lang][key]);
    const setField = (key) => (e) => {
        const value = e.target.value;
        if (lang === "ru") setForm((f) => ({ ...f, [key]: value }));
        else setTr((t) => ({ ...t, [lang]: { ...t[lang], [key]: value } }));
    };
    // image: { preview, url } — новое; null — без изменений; false — убрать
    const [image, setImage] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && !saving && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [saving, onClose]);

    const attach = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) return setError("Можно прикрепить только изображение.");
        if (file.size > MAX_IMAGE_MB * 1024 * 1024) return setError(`Изображение больше ${MAX_IMAGE_MB} МБ.`);
        setError(null);
        setUploading(true);
        const preview = URL.createObjectURL(file);
        try {
            const url = await uploadChatFile(file);
            setImage({ preview, url });
        } catch (err) {
            setError(err.message || "Ошибка загрузки изображения");
        } finally {
            setUploading(false);
        }
    };

    const payload = {};
    if (form.title.trim() !== notification.title) payload.title = form.title.trim();
    if (form.text.trim() !== notification.text) payload.text = form.text.trim();
    if (form.type !== currentType) payload.notification_type = form.type;
    if (image === false) payload.file = "";
    else if (image?.url) payload.file = image.url;
    const trChanged = ["kk", "en"].some((l) => tr[l].title.trim() !== initialTr[l].title.trim() || tr[l].text.trim() !== initialTr[l].text.trim());
    if (trChanged) {
        payload.translations = Object.fromEntries(
            ["kk", "en"].filter((l) => status[l] === "filled").map((l) => [l, { title: tr[l].title.trim(), text: tr[l].text.trim() }])
        );
    }
    const dirty = Object.keys(payload).length > 0;

    const save = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.text.trim()) return setError("Заголовок и текст на русском (RU) не могут быть пустыми.");
        if (partialLang) return setError(`${partialLang === "kk" ? "KZ" : "EN"}: заполните и заголовок, и текст — или оставьте оба пустыми.`);
        if (!dirty) return onClose();
        setSaving(true);
        setError(null);
        try {
            await editNotification(notification.id, payload);
            onSaved(
                {
                    ...notification,
                    title: form.title.trim(),
                    text: form.text.trim(),
                    ...(payload.file !== undefined ? { file: payload.file } : {}),
                    ...(payload.translations ? { translations: { ru: { title: form.title.trim(), text: form.text.trim() }, ...payload.translations } } : {}),
                },
                payload.notification_type
            );
        } catch (err) {
            setError(err.message);
            setSaving(false);
        }
    };

    const shownImage = image === false ? null : image?.preview || imageSrc(notification.file) || null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            role="presentation"
            onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}
        >
            <form onSubmit={save} role="dialog" aria-modal="true" className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-block border border-border-primary bg-bg-card text-left shadow-lg">
                <div className="flex items-center justify-between border-b border-border-primary px-5 py-3.5">
                    <h3 className="text-base font-semibold text-text-primary">
                        Редактирование <span className="font-mono text-text-muted">#{notification.id}</span>
                    </h3>
                    <button type="button" onClick={onClose} aria-label="Закрыть" className="rounded-lg p-1 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-4 overflow-y-auto p-5">
                    <div>
                        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => attach(e.target.files?.[0])} />
                        {shownImage ? (
                            <div className="relative overflow-hidden rounded-lg">
                                <img src={shownImage} alt="" className="aspect-video w-full object-cover" />
                                <div className="absolute right-2 top-2 flex gap-1.5">
                                    <button type="button" onClick={() => fileInputRef.current?.click()} className="rounded-lg bg-black/60 px-2.5 py-1 text-xs font-medium text-white hover:bg-black/75">
                                        Заменить
                                    </button>
                                    <button type="button" onClick={() => setImage(false)} className="rounded-lg bg-black/60 px-2.5 py-1 text-xs font-medium text-white hover:bg-rose-600">
                                        Убрать
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex aspect-video w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border-primary text-sm text-text-secondary hover:bg-bg-tertiary"
                            >
                                <PhotoIcon className="h-7 w-7" />
                                Добавить изображение
                            </button>
                        )}
                        {uploading && (
                            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-text-secondary">
                                <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Загрузка изображения…
                            </p>
                        )}
                    </div>

                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-text-secondary">Раздел</span>
                        <div className="flex gap-1.5">
                            {TYPES.map((t) => (
                                <button
                                    key={t.value}
                                    type="button"
                                    onClick={() => setForm((f) => ({ ...f, type: t.value }))}
                                    className={`flex-1 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                        form.type === t.value ? "border-[#4F45E5] bg-[#4F45E5]/10 text-text-primary" : "border-border-primary text-text-secondary hover:bg-bg-tertiary"
                                    }`}
                                >
                                    {form.type === t.value && <CheckIcon className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />}
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </label>

                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <LangTabs value={lang} onChange={setLang} status={status} />
                        {lang !== "ru" && status[lang] === "empty" && <span className="text-xs text-text-muted">Пусто — покажем RU</span>}
                    </div>

                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-text-secondary">Заголовок · {lang === "kk" ? "KZ" : lang.toUpperCase()}</span>
                        <input autoFocus type="text" value={fieldValue("title")} onChange={setField("title")} className={inputCls} />
                    </label>

                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-text-secondary">Текст · {lang === "kk" ? "KZ" : lang.toUpperCase()}</span>
                        <textarea rows={6} value={fieldValue("text")} onChange={setField("text")} className={`${inputCls} resize-y`} />
                    </label>

                    <p className="text-xs text-text-muted">Изменения появятся в ленте. Push-уведомление повторно не отправляется.</p>

                    {error && (
                        <div role="alert" className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-500">
                            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                            {error}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 border-t border-border-primary px-5 py-3">
                    <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
                        Отмена
                    </button>
                    <button
                        type="submit"
                        disabled={saving || uploading || !dirty}
                        className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    >
                        {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                        {dirty ? "Сохранить" : "Нет изменений"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ManageNotifications;
